/**
 * Routing recommendations exported by the G4 provider cost-efficiency report, for the G11
 * routing preferences to consume later (stated preferences win; these are the learned inputs).
 * Computed and held on the endpoint: it never travels through the hub. Versioned; additive only.
 */
import { z } from 'zod';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const RoutingCandidate = z.strictObject({
    provider: z.enum(['claude', 'codex']),
    model: z.string().min(1).max(120).optional(),
});
/** How trustworthy the cost behind a figure is (M15 kinds, never blended into one number). */
export const RoutingCostBasis = z.enum(['reported', 'estimate', 'subscription', 'mixed']);
export const RoutingEvidence = z.strictObject({
    runs: z.number().int().nonnegative(),
    verifiedOutcomes: z.number().int().nonnegative(),
    /** Outcomes weighted down by corrections; equals `verifiedOutcomes` when corrections are unknown. */
    qualityAdjustedOutcomes: z.number().nonnegative(),
    correctionsKnown: z.boolean(),
    costBasis: RoutingCostBasis,
    /** Share of runs with any cost figure (reported, estimated or amortized). */
    costCoverage: z.number().min(0).max(1),
    usdPerQualityAdjustedOutcome: z.number().nonnegative(),
});
export const RoutingRecommendation = z.strictObject({
    taskType: z.string().min(1).max(60),
    preferred: RoutingCandidate,
    /** Best account for the preferred provider/model, when accounts differ in cost. */
    accountId: z.string().min(1).max(160).optional(),
    evidence: RoutingEvidence,
    /** The runner-up the preference beat, and by how much (percent cheaper per outcome). */
    beat: z.strictObject({ candidate: RoutingCandidate, cheaperPct: z.number().min(0) }),
    confidence: z.enum(['low', 'medium', 'high']),
    reason: z.string().min(1).max(500),
});
export const RoutingRecommendations = z.strictObject({
    version: z.literal(1),
    source: z.literal('g4-cost-efficiency'),
    generatedAt: Ts,
    windowDays: z.number().int().positive().max(366),
    /** Date of the API price table behind every `estimate` figure. */
    priceTableAsOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    recommendations: z.array(RoutingRecommendation).max(200),
});
//# sourceMappingURL=routing-recommendations.js.map